import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  Flame, 
  Phone, 
  MessageCircle,
  ChevronRight,
  MapPin,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const HotDeals = ({ user }) => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHotDeals = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/hot-deals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeals(data.leads || []);
      }
    } catch (error) {
      toast.error('Failed to load hot deals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHotDeals();
  }, [fetchHotDeals]);

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Visit Planned': return 'bg-blue-100 text-blue-700';
      case 'Visited': return 'bg-emerald-100 text-emerald-700';
      case 'Booking Requested': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header - Standardized ← Title format (gradient variant) */}
      <div className="bg-gradient-to-r from-[#5B3DF5] to-orange-500 text-white">
        <div className="px-4 py-4 flex items-center gap-2">
          <button 
            onClick={() => navigate(-1)} 
            className="p-1.5 -ml-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="page-back-btn"
          >
            <ArrowLeft size={20} className="text-white" strokeWidth={2.5} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Flame size={20} /> Hot Deals
            </h1>
            <p className="text-xs text-white/70">{deals.length} active opportunities</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EF4444] mx-auto mb-3"></div>
            <p className="text-sm text-slate-500">Loading hot deals...</p>
          </div>
        ) : deals.length === 0 ? (
          <Card className="p-8 text-center bg-white border-0 shadow-sm rounded-xl">
            <Flame size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">No hot deals right now</p>
            <p className="text-sm text-slate-400">Keep nurturing your leads!</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {deals.map((deal) => (
              <Card 
                key={deal.lead_id}
                className="p-4 bg-white border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0" onClick={() => navigate(`/crm/${deal.lead_id}`)}>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800 truncate">{deal.customer_name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStageColor(deal.stage)}`}>
                        {deal.stage}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{deal.phone}</p>
                    {deal.project_name && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                        <MapPin size={12} />
                        <span>{deal.project_name}</span>
                      </div>
                    )}
                    {deal.next_followup && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                        <Calendar size={12} />
                        <span>Follow-up: {formatDate(deal.next_followup)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-9 w-9"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`tel:${deal.phone}`, '_self');
                        }}
                      >
                        <Phone size={16} />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-9 w-9"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`https://wa.me/91${deal.phone?.replace(/\D/g, '')}`, '_blank');
                        }}
                      >
                        <MessageCircle size={16} />
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => navigate(`/crm/${deal.lead_id}`)}
                    >
                      Open <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HotDeals;
